// Server-synced subscription state for the authenticated user.
//
// Mirrors the `userSettingsStore` pattern (devtools + subscribeWithSelector +
// immer; per-operation timers; `actions` namespace; `registerXxxHandlers`
// exporter). `tierName` is the DISPLAY tier, plus the Docling engine-lane
// fallback when the server sent no gate tier (`gateTier || tierName` in
// `DocumentConversion` and `ConversionChoiceDialog`). What a plan unlocks is NOT
// read from here: it comes from `features/billing/entitlementStore`'s
// `capabilities` / `gateTier` (BILL-20a).
//
// It is NOT the source for anything the user reads as their billing state, and
// NOT the subscription gate. The backend endpoint
// (`internal/auth/subscription_handler.go`) is served by `billingTierSource`
// (`services/server/boot/tier_source.go`) over `UserTierResolver.Resolve`, and
// on a resolver error it fails SOFT to Minimum / ACTIVE — right for tier
// gating (never 500 a settings page), wrong as a billing fact or an
// entitlement verdict. User-facing billing state is the HTTP
// `useSubscriptions()` path used by `/billing`; whether the user may use the
// app at all is `features/billing/entitlementStore` (BILL-03), read from the
// server's `/api/v1/billing/entitlement`. Do not re-surface this store's
// values as either.
import { create as createProto } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createEnvelope } from '@/lib/protobuf/codec';
import {
  GetSubscriptionStateRequestSchema,
  SubscriptionStatus,
} from '@/lib/protobuf/gen/auth/auth_pb';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  createRequestTimers,
  timeoutError,
} from '@/lib/websocket/requestTimeout';
import { wsManager } from '@/lib/websocket';
import type { AsyncState } from '@/types/common';

interface SubscriptionStoreState {
  tierName: string;
  status: SubscriptionStatus;
  renewsAt: number;        // unix seconds; 0 if N/A. proto int64 → JS number (safe up to 2^53).
  polarCustomerId: string;
  loadState: AsyncState<void>;

  actions: {
    load: () => void;
    handleResponse: (envelope: MessageEnvelope) => void;
  };
}

const timers = createRequestTimers<'load'>();

export const useSubscriptionStore = create<SubscriptionStoreState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        tierName: '',
        status: SubscriptionStatus.UNSPECIFIED,
        renewsAt: 0,
        polarCustomerId: '',
        loadState: { status: 'idle' } as AsyncState<void>,

        actions: {
          load: () => {
            set((state) => {
              state.loadState = { status: 'loading' };
            });
            timers.arm('load', DEFAULT_REQUEST_TIMEOUT_MS, () => {
              if (get().loadState.status === 'loading') {
                set((state) => {
                  state.loadState = {
                    status: 'error',
                    error: timeoutError('load subscription state'),
                  };
                });
              }
            });
            const req = createProto(GetSubscriptionStateRequestSchema, {});
            wsManager.sendEnvelope(
              createEnvelope({ case: 'getSubscriptionStateRequest', value: req }),
            );
          },

          handleResponse: (envelope) => {
            if (envelope.payload.case !== 'subscriptionStateResponse') return;
            const val = envelope.payload.value;
            timers.clear('load');
            set((state) => {
              state.tierName = val.tierName ?? '';
              state.status = val.status ?? SubscriptionStatus.UNSPECIFIED;
              // protobuf-es maps int64 to bigint; coerce to JS number (unix
              // seconds always fit in 2^53). Guard against the response
              // shape arriving as a plain number too (test fixtures).
              const rawRenews = val.renewsAt;
              state.renewsAt =
                typeof rawRenews === 'bigint'
                  ? Number(rawRenews)
                  : typeof rawRenews === 'number'
                    ? rawRenews
                    : 0;
              state.polarCustomerId = val.polarCustomerId ?? '';
              state.loadState = { status: 'success', data: undefined };
            });
          },
        },
      })),
    ),
    { name: 'SubscriptionStore' },
  ),
);

export function registerSubscriptionHandlers(): () => void {
  const { actions } = useSubscriptionStore.getState();
  const u1 = wsManager.onMessage('subscriptionStateResponse', actions.handleResponse);
  // Refresh on tier change — re-run load() to pull fresh state from the server.
  const u2 = wsManager.onMessage('tierChangedEvent', () => {
    actions.load();
  });
  return () => {
    u1();
    u2();
  };
}
